import { useState } from 'react';
import { Currency, CurrencyCreate } from '../types/costwand.d';
import { useCurrencies } from '../hooks/useCurrencies.ts';
import CurrencyForm from '../components/CurrencyForm.tsx';
import CurrenciesTable from '../components/CurrenciesTable.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  // DialogFooter, // Removed DialogFooter as it's not used for the form anymore
} from '../components/ui/dialog.tsx';
// import { useError } from '../context/ErrorContext.tsx'; // Not needed as apiClient handles global error

const CurrenciesPage = () => {
  const {
    currencies,
    loading,
    error, // This error is for the overall fetch, not form submission
    currentPage,
    totalPages,
    totalItems, // Destructure totalItems from the hook
    handlePageChange,
    createCurrency,
    updateCurrency,
    deleteCurrency,
    fetchCurrencies, // Assuming useCurrencies provides a way to refetch
  } = useCurrencies();

  // const { displayError } = useError(); // Not needed as apiClient handles global error

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [editingCurrency, setEditingCurrency] = useState<Currency | null>(null);
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);

  // Removed states for the separate error dialog for form submissions
  // const [isErrorDialogOpen, setIsErrorDialogOpen] = useState<boolean>(false);
  // const [errorDialogMessage, setErrorDialogMessage] = useState<string | null>(null);

  const handleAddCurrency = () => {
    setEditingCurrency(null);
    setIsFormOpen(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  const handleEditCurrency = (currency: Currency) => {
    setEditingCurrency(currency);
    setIsFormOpen(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  const handleDeleteCurrency = async (id: number) => {
    // The useCurrencies hook should handle its own loading/error for delete
    await deleteCurrency(id);
    // Refetch currencies after deletion to ensure the list is up-to-date
    fetchCurrencies(currentPage);
  };

  const handleFormSubmit = async (data: CurrencyCreate) => {
    setFormSubmitting(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);

    try {
      let success = false;
      if (editingCurrency) {
        success = await updateCurrency(editingCurrency.id, data);
      } else {
        success = await createCurrency(data);
      }

      if (success) {
        setIsFormOpen(false); // Close form dialog on success
        fetchCurrencies(currentPage); // Re-fetch to update the list
      } else {
        // The global error handler in apiClient would have already displayed the error.
        // We just need to ensure the form stays open if it failed.
        // displayError(error || "An unexpected error occurred during form submission."); // If we wanted to explicitly trigger global error here
        // setIsErrorDialogOpen(true); // Removed local error dialog trigger
      }
    } catch (e: any) {
      // This catch block is for unexpected errors not handled by the hook's return value
      // The global error handler in apiClient would have already displayed the error.
      // displayError(e.message || "An unexpected error occurred."); // If we wanted to explicitly trigger global error here
      // setIsErrorDialogOpen(true); // Removed local error dialog trigger
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingCurrency(null);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  // Removed handleCloseErrorDialog function

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-secondary-foreground">Currency Management</h2>
        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogTrigger asChild>
            <Button onClick={handleAddCurrency}>Add New Currency</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>{editingCurrency ? 'Edit Currency' : 'Add New Currency'}</DialogTitle>
              <DialogDescription>
                {editingCurrency ? 'Make changes to the currency here.' : 'Add a new currency to your list.'}
              </DialogDescription>
            </DialogHeader>
            <CurrencyForm
              initialData={editingCurrency}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              isSubmitting={formSubmitting}
              // The 'error' prop is removed from CurrencyForm as errors are now shown in a separate dialog
            />
          </DialogContent>
        </Dialog>
      </div>

      <CurrenciesTable
        currencies={currencies}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems} // Pass totalItems to the table
        onPageChange={handlePageChange}
        onEdit={handleEditCurrency}
        onDelete={handleDeleteCurrency}
        loading={loading}
        error={error} // This error is for the table's data fetching
        baseEndpoint="/currencies" // NEW: Pass the base endpoint
      />

      {/* Removed Error Dialog for form submission errors */}
      {/*
      <Dialog open={isErrorDialogOpen} onOpenChange={setIsErrorDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-destructive">Error</DialogTitle>
            <DialogDescription>
              An error occurred during the operation.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 text-red-600">
            {errorDialogMessage || 'An unexpected error occurred.'}
          </div>
          <DialogFooter>
            <Button onClick={handleCloseErrorDialog}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      */}
    </div>
  );
};

export default CurrenciesPage;